import * as React from 'react';

import { ContentCard, ContentEmpty, Loading } from '@rematter/ui-web';
import { Badge, SearchField, Tab } from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';

import * as state from '../../_state/entityLoads.state';
import * as loadDetailsState from '../../_state/loadDetails.state';
import {
  canLoadBeMarkedAsReceived,
  canLoadBeMarkedAsReviewed,
} from '../../utils/loadUtils';

import { EntityLoadCard } from './EntityLoadCard';
import { EntityLoadTabMaterials } from './EntityLoadTabMaterials';
import { EntityLoadTabPhotos } from './EntityLoadTabPhotos';
import { MarkLoadAsReceivedButton } from './MarkLoadAsReceived';
import { MarkLoadAsReviewedButton } from './MarkLoadAsReviewed';

import { useDebouncedAtom } from '~/shared/hooks/useDebouncedAtom';

export const EntityTabLoads = () => {
  const [searchTerm, setSearchTerm] = useDebouncedAtom(state.$searchTerm, 300);

  const bookingId = useAtomValue(state.$bookingId);
  const allLoads = useAtomValue(state.$allEntityLoads);
  const { data: loads = [], isPending: isLoadingListOfLoads } = useAtomValue(
    state.$filteredEntityLoads,
  );

  const { data: currentLoad, isLoading: isLoadingCurrentLoad } = useAtomValue(
    loadDetailsState.$loadDetails,
  );

  const loadCanBeMarkedAsReceived = canLoadBeMarkedAsReceived(currentLoad);
  const loadCanBeMarkedAsReviewed = canLoadBeMarkedAsReviewed(currentLoad);

  const isLoading = isLoadingListOfLoads || isLoadingCurrentLoad;

  const [selectedLoadId, setSelectedLoadId] = useAtom(state.$selectedLoadId);
  const [loadTab = 'materials', setLoadTab] = useAtom(state.$loadTab);

  const hasLoads = allLoads.length > 0;

  // Auto select the first load if no load is selected
  React.useEffect(() => {
    if (loads.length > 0 && !selectedLoadId) {
      setSelectedLoadId(loads[0].id);
    }
  }, [loads, selectedLoadId, setSelectedLoadId]);

  return (
    <div className="@EntityTabLoads grid h-full grid-cols-[240px_auto]">
      {isLoading && !hasLoads && (
        <div className="col-span-2 flex justify-center p-10">
          <Loading isLoading />
        </div>
      )}
      {!hasLoads && !isLoading && (
        <div className="col-span-2 p-5">
          <ContentEmpty>
            This {bookingId ? 'booking' : 'purchase order'} has no loads yet
          </ContentEmpty>
        </div>
      )}
      {hasLoads && (
        <>
          <div className="grid size-full grid-rows-[auto_1fr] overflow-y-auto">
            <div className="p-4">
              <SearchField
                placeholder="Search loads"
                value={searchTerm}
                onChange={setSearchTerm}
              />
            </div>
            <div className="no-scrollbar flex h-full shrink-0 flex-col gap-2 overflow-y-auto p-4 pt-0">
              {loads.map((load) => (
                <EntityLoadCard key={load.id} load={load} />
              ))}
            </div>
          </div>
          {currentLoad && (
            <div className="flex h-full overflow-y-auto p-4 pl-0">
              <ContentCard className="flex size-full flex-col p-0">
                <div className="flex h-[50px] shrink-0 items-center justify-between p-4 pb-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">
                      Load #{currentLoad?.loadNumber}
                    </span>
                    <Badge size="small">{currentLoad?.status}</Badge>
                  </div>
                  {loadCanBeMarkedAsReceived && (
                    <MarkLoadAsReceivedButton loadIds={[currentLoad.id]} />
                  )}
                  {loadCanBeMarkedAsReviewed && (
                    <MarkLoadAsReviewedButton loadId={currentLoad.id} />
                  )}
                </div>
                <div className="flex shrink-0 gap-5 border-b border-neutral/10 px-4">
                  <Tab
                    selected={loadTab === 'materials'}
                    onClick={() => setLoadTab('materials')}
                  >
                    <span>Materials</span>
                  </Tab>
                  <Tab
                    selected={loadTab === 'photos'}
                    onClick={() => setLoadTab('photos')}
                  >
                    <span>Photos</span>
                  </Tab>
                </div>
                <div className="no-scrollbar h-full overflow-y-auto p-4">
                  {loadTab === 'materials' && <EntityLoadTabMaterials />}
                  {loadTab === 'photos' && <EntityLoadTabPhotos />}
                </div>
              </ContentCard>
            </div>
          )}
        </>
      )}
    </div>
  );
};
